// components/collab-projects/CollabProjectsProvider.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { db } from "../../firebase/firebase.utils";
import { setSpaceData, setStationData } from "../../redux/space/space.actions";
import { setLoading } from "../../redux/history/history.actions";
import { setOpen } from "../../redux/user/user.actions";
const Context = createContext(null);
const EMPTY = [];
const validId = (id) =>
  typeof id === "string" && id.length > 0 && !id.includes("/");
export const projectKey = (spaceId, stationId) =>
  JSON.stringify([spaceId, stationId]);
export const projectUrl = (project) =>
  `/s/${encodeURIComponent(project.spaceId)}/e/${encodeURIComponent(project.stationId)}/b`;
export function useCollabProjects() {
  const value = useContext(Context);
  if (!value)
    throw new Error("Mount CollabProjectsProvider around ChatLayout.");
  return value;
}
// One provider owns navigation data, even when both the sidebar and popover render.
export default function CollabProjectsProvider({ children }) {
  const uid = useSelector((s) => s.user.currentUser?.uid);
  const users = useSelector((s) => s.user.users) || EMPTY;
  const dispatch = useDispatch();
  const [directory, setDirectory] = useState({
    uid: null,
    refs: [],
    ready: false,
  });
  const [workspaceState, setWorkspaceState] = useState({
    uid: null,
    spaces: [],
    ready: false,
  });
  const [workspaceProjects, setWorkspaceProjects] = useState({
    uid: null,
    records: {},
  });
  const [shared, setShared] = useState({ uid: null, records: {} });
  const [loadedPeople, setLoadedPeople] = useState({ uid: null, records: {} });
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    const projectUnsubs = new Map();
    setError("");
    dispatch(setSpaceData([]));
    dispatch(setStationData([]));
    dispatch(setLoading(Boolean(uid)));
    setDirectory({ uid, refs: [], ready: !uid });
    setWorkspaceState({ uid, spaces: [], ready: !uid });
    setWorkspaceProjects({ uid, records: {} });
    setShared({ uid, records: {} });
    setLoadedPeople({ uid, records: {} });
    if (!uid) return;
    const fail = (message) => {
      if (active) setError(message);
    };
    const stopUser = db
      .collection("users")
      .doc(uid)
      .onSnapshot(
        (snap) => {
          if (!active) return;
          const data = snap.exists ? snap.data() : {};
          const refs = new Map();
          (Array.isArray(data.sharedWith) ? data.sharedWith : []).forEach(
            (ref) => {
              if (validId(ref?.fromSpaceId) && validId(ref?.stationId)) {
                refs.set(projectKey(ref.fromSpaceId, ref.stationId), {
                  spaceId: ref.fromSpaceId,
                  stationId: ref.stationId,
                  sharedBy: validId(ref.invitedBy) ? ref.invitedBy : "",
                });
              }
            },
          );
          setDirectory({
            uid,
            refs: [...refs.values()],
            ready: true,
            showWorkspaceProjects:
              data.preferences?.showWorkspaceCollabProjects === true,
          });
          dispatch(setOpen(Boolean(data.open)));
        },
        () => {
          if (!active) return;
          setDirectory({ uid, refs: [], ready: true });
          fail("Could not load your shared project list.");
        },
      );
    const stopSpaces = db
      .collection("space")
      .where("members", "array-contains", uid)
      .onSnapshot(
        (snap) => {
          if (!active) return;
          const spaces = snap.docs.map((d) => ({
            ...d.data(),
            id: d.id,
            spaceId: d.id,
          }));
          const ids = new Set(spaces.map((s) => s.spaceId));
          for (const [id, stop] of projectUnsubs) {
            if (!ids.has(id)) {
              stop();
              projectUnsubs.delete(id);
            }
          }
          setWorkspaceProjects((old) => ({
            uid,
            records: Object.fromEntries(
              Object.entries(old.uid === uid ? old.records : {}).filter(
                ([id]) => ids.has(id),
              ),
            ),
          }));
          spaces.forEach(({ spaceId }) => {
            if (projectUnsubs.has(spaceId)) return;
            const stop = db
              .collection("space")
              .doc(spaceId)
              .collection("stations")
              .orderBy("name", "asc")
              .onSnapshot(
                (projects) => {
                  if (!active || !projectUnsubs.has(spaceId)) return;
                  const records = projects.docs.map((d) => ({
                    ...d.data(),
                    id: d.id,
                    stationId: d.id,
                    spaceId,
                    fromSpaceId: spaceId,
                  }));
                  setWorkspaceProjects((old) => ({
                    uid,
                    records: { ...old.records, [spaceId]: records },
                  }));
                },
                () => {
                  if (!active) return;
                  setWorkspaceProjects((old) => ({
                    uid,
                    records: { ...old.records, [spaceId]: [] },
                  }));
                  fail("Some workspace projects could not be loaded.");
                },
              );
            projectUnsubs.set(spaceId, stop);
          });
          setWorkspaceState({ uid, spaces, ready: true });
          dispatch(setSpaceData(spaces));
          dispatch(setLoading(false));
        },
        () => {
          if (!active) return;
          projectUnsubs.forEach((stop) => stop());
          projectUnsubs.clear();
          setWorkspaceProjects({ uid, records: {} });
          setWorkspaceState({ uid, spaces: [], ready: true });
          dispatch(setSpaceData([]));
          dispatch(setLoading(false));
          fail(
            "Could not load workspaces. Directly shared projects can still load.",
          );
        },
      );
    return () => {
      active = false;
      stopUser();
      stopSpaces();
      projectUnsubs.forEach((stop) => stop());
    };
  }, [uid, dispatch]);
  const refs = directory.uid === uid ? directory.refs : EMPTY;
  const pathsKey = JSON.stringify(
    refs.map((p) => [p.spaceId, p.stationId]).sort(),
  );
  useEffect(() => {
    let active = true;
    const paths = JSON.parse(pathsKey);
    setShared({ uid, records: {} });
    if (!uid) return;
    const stops = paths.map(([spaceId, stationId]) =>
      db
        .collection("space")
        .doc(spaceId)
        .collection("stations")
        .doc(stationId)
        .onSnapshot(
          (snap) => {
            if (!active) return;
            const data = snap.exists ? snap.data() : null;
            const project =
              Array.isArray(data?.members) && data.members.includes(uid)
                ? {
                    ...data,
                    id: stationId,
                    stationId,
                    spaceId,
                    fromSpaceId: spaceId,
                  }
                : null;
            setShared((old) => ({
              uid,
              records: {
                ...old.records,
                [projectKey(spaceId, stationId)]: project,
              },
            }));
          },
          (err) => {
            if (!active) return;
            setShared((old) => ({
              uid,
              records: {
                ...old.records,
                [projectKey(spaceId, stationId)]: null,
              },
            }));
            if (err.code !== "permission-denied")
              setError("A shared project could not be loaded.");
          },
        ),
    );
    return () => {
      active = false;
      stops.forEach((stop) => stop());
    };
  }, [uid, pathsKey]);
  const spaces = workspaceState.uid === uid ? workspaceState.spaces : EMPTY;
  const directProjects = useMemo(
    () =>
      refs.flatMap((ref) => {
        const project =
          shared.uid === uid &&
          shared.records[projectKey(ref.spaceId, ref.stationId)];
        return project ? [{ ...project, sharedBy: ref.sharedBy }] : [];
      }),
    [uid, refs, shared],
  );
  const allProjects = useMemo(() => {
    const records = new Map(
      directProjects.map((p) => [projectKey(p.spaceId, p.stationId), p]),
    );
    if (workspaceProjects.uid === uid)
      spaces.forEach((space) => {
        (workspaceProjects.records[space.spaceId] || []).forEach((p) =>
          records.set(projectKey(p.spaceId, p.stationId), p),
        );
      });
    return [...records.values()];
  }, [uid, directProjects, spaces, workspaceProjects]);
  useEffect(() => {
    dispatch(setStationData(allProjects));
  }, [dispatch, allProjects]);
  const people = useMemo(() => {
    const result = {
      ...(loadedPeople.uid === uid ? loadedPeople.records : {}),
    };
    users.forEach((user) => {
      if (user?.uid) result[user.uid] = user;
    });
    return result;
  }, [uid, users, loadedPeople]);
  const missingPeopleKey = JSON.stringify(
    [...new Set(refs.map((p) => p.sharedBy).filter(Boolean))]
      .filter((id) => !(id in people))
      .sort(),
  );
  useEffect(() => {
    let active = true;
    const ids = JSON.parse(missingPeopleKey);
    if (!uid || !ids.length) return;
    Promise.all(
      ids.map(async (id) => {
        try {
          const snap = await db.collection("users").doc(id).get();
          return [id, snap.exists ? { ...snap.data(), uid: id } : null];
        } catch {
          return [id, null];
        }
      }),
    ).then((entries) => {
      if (active)
        setLoadedPeople((old) => ({
          uid,
          records: { ...old.records, ...Object.fromEntries(entries) },
        }));
    });
    return () => {
      active = false;
    };
  }, [uid, missingPeopleKey]);
  const workspaceIds = new Set(spaces.map((s) => s.spaceId));
  const showWorkspaceProjects =
    directory.uid === uid && directory.showWorkspaceProjects === true;
  const projects = directProjects.filter(
    (p) => showWorkspaceProjects || !workspaceIds.has(p.spaceId),
  );
  const setShowWorkspaceProjects = async (value) => {
    if (!uid) return;
    await db
      .collection("users")
      .doc(uid)
      .update({ "preferences.showWorkspaceCollabProjects": value });
  };
  const loading =
    directory.uid !== uid ||
    !directory.ready ||
    workspaceState.uid !== uid ||
    !workspaceState.ready ||
    refs.some(
      (p) =>
        shared.uid !== uid ||
        !(projectKey(p.spaceId, p.stationId) in shared.records),
    );
  return (
    <Context.Provider
      value={{
        uid,
        projects,
        showWorkspaceProjects,
        setShowWorkspaceProjects,
        spaces,
      allProjects,
        people,
        loading,
        error,
        workspacesReady: workspaceState.uid === uid && workspaceState.ready,
      }}
    >
      {children}
    </Context.Provider>
  );
}
